import { Locale } from '@headless-workspace/config';
import {
    Repository,
    Result,
    ResultFailureFromApiError,
    ResultSuccess,
    RevalidationServerActionType,
} from '@headless-workspace/core-domain';
import {
    memoryCacheAuthStrategy,
    memoryCacheQueryParamStrategy,
    memoryCacheRefreshTokenStrategy,
    RequestStrategy,
} from '@headless-workspace/data';
import { VotingType } from '../../types';
import { RatingsAndReviewsDTO } from './RatingsAndReviewsDTO';
import {
    mapRatingsAndReviewsValueWithDTOSortingOptions,
    RatingsAndReviewsValueWithDTOSortingOptions,
} from './RatingsAndReviewsValue';
import { ReviewPayload } from './types';

const enum FeedbackTypeValue {
    Helpfulness = 'helpfulness',
    Report = 'inappropriate',
}

export interface ReviewsRepositorySpec {
    getReviews(
        locale: Locale,
        productId: string,
        offset: number,
        limit: number,
        filters?: string,
        sort?: string,
    ): Promise<Result<RatingsAndReviewsValueWithDTOSortingOptions>>;

    evaluateReview(locale: Locale, reviewId: string, vote: VotingType): Promise<Result<void>>;

    reportReview(locale: Locale, reviewId: string): Promise<Result<void>>;

    postReview(locale: Locale, productId: string, reviewPayload: ReviewPayload): Promise<Result<boolean>>;
}

export class ReviewsRepository extends Repository implements ReviewsRepositorySpec {
    path = '/catalog/v1/reviews';

    constructor(
        requestStrategy: RevalidationServerActionType & RequestStrategy = {
            authStrategy: memoryCacheAuthStrategy,
            refreshTokenStrategy: memoryCacheRefreshTokenStrategy,
            queryParamStrategy: memoryCacheQueryParamStrategy,
            revalidationKey: undefined, // This can be set if needed for revalidation
        },
    ) {
        super(requestStrategy.authStrategy, requestStrategy.refreshTokenStrategy, requestStrategy.queryParamStrategy);
    }

    async getReviews(
        locale: Locale,
        productId: string,
        offset: number,
        limit: number,
        filters?: string,
        sort?: string,
    ): Promise<Result<RatingsAndReviewsValueWithDTOSortingOptions>> {
        try {
            const params: Record<string, unknown> = {
                locale,
                productId,
                offset,
                limit,
            };

            if (filters) params.filters = filters;
            if (sort) params.sort = sort;

            const response = await this.datasource.getResource(this.path, params);

            const ratingsAndReviews = RatingsAndReviewsDTO.parse(response);

            return ResultSuccess(mapRatingsAndReviewsValueWithDTOSortingOptions(ratingsAndReviews, locale));
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { locale });
        }
    }

    async evaluateReview(locale: Locale, reviewId: string, vote: VotingType): Promise<Result<void>> {
        try {
            const params = {
                locale,
            };
            const body = {
                vote,
                type: FeedbackTypeValue.Helpfulness,
            };

            void this.datasource.createResource(`${this.path}/${reviewId}/feedback`, body, params);
            return ResultSuccess(void 0);
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { locale });
        }
    }

    async reportReview(locale: Locale, reviewId: string): Promise<Result<void>> {
        try {
            const params = {
                locale,
            };
            const body = {
                type: FeedbackTypeValue.Report,
            };

            void this.datasource.createResource(`${this.path}/${reviewId}/feedback`, body, params);
            return ResultSuccess(void 0);
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { locale });
        }
    }

    async postReview(locale: Locale, productId: string, reviewPayload: ReviewPayload): Promise<Result<boolean>> {
        try {
            await this.datasource.createResource(`${this.path}`, reviewPayload, {
                productId,
                locale,
            });
            return ResultSuccess(true);
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { locale });
        }
    }
}
